import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck, UserRound, Users } from "lucide-react";
import { Card } from "#/components/common/Card";
import { PageLoader } from "#/components/common/PageLoader";
import { StatusBadge } from "#/components/common/StatusBadge";
import { ErrorState } from "#/components/common/ErrorState";
import { useUser } from "../hooks";

function listOrDash(values?: string[]) {
  return values?.length ? values : [];
}

export default function UserViewPage() {
  const { userId } = useParams();
  const id = Number(userId);

  const { data: user, isLoading, error } = useUser(id);

  if (isLoading) return <PageLoader label="Loading user..." />;

  if (!Number.isFinite(id) || error || !user) {
    return <ErrorState message="Failed to load user details." />;
  }

  const roles = user.roleCodes?.length ? user.roleCodes : (user.roles ?? []);

  const departments = user.departments?.length
    ? user.departments.map((department) => department.name || department.code)
    : user.departmentCodes?.length
      ? user.departmentCodes
      : user.employee?.departments?.length
        ? user.employee.departments.map(
            (department) => department.name || department.code,
          )
        : [];

  const permissions = listOrDash(user.permissions);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to users
        </Link>

        <h1 className="mt-3 text-2xl font-bold text-slate-900">
          {user.fullName || user.username}
        </h1>
      </div>

      <Card className="p-6">
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase text-slate-500">
              Username
            </p>
            <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-900">
              <UserRound className="h-4 w-4 text-emerald-700" />
              {user.username}
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase text-slate-500">
              Email
            </p>
            <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Mail className="h-4 w-4 text-emerald-700" />
              {user.email}
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase text-slate-500">
              Full name
            </p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {user.fullName || user.employee?.fullName || "-"}
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase text-slate-500">
              Account status
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <StatusBadge
                label={user.enabled ? "Enabled" : "Disabled"}
                tone={user.enabled ? "success" : "danger"}
              />
              <StatusBadge
                label={user.locked ? "Locked" : "Unlocked"}
                tone={user.locked ? "warning" : "neutral"}
              />
              <StatusBadge
                label={
                  user.mustChangePassword
                    ? "Password change required"
                    : "Password OK"
                }
                tone={user.mustChangePassword ? "warning" : "success"}
              />
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-700" />
            <h2 className="text-lg font-semibold text-slate-900">
              Departments
            </h2>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {departments.length ? (
              departments.map((department) => (
                <span
                  key={department}
                  className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700"
                >
                  {department}
                </span>
              ))
            ) : (
              <p className="text-sm text-slate-500">No departments assigned.</p>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-700" />
            <h2 className="text-lg font-semibold text-slate-900">Roles</h2>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {roles.length ? (
              roles.map((role) => (
                <span
                  key={role}
                  className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"
                >
                  {role}
                </span>
              ))
            ) : (
              <p className="text-sm text-slate-500">No roles assigned.</p>
            )}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-slate-900">Permissions</h2>

        <div className="mt-4 flex flex-wrap gap-2">
          {permissions.length ? (
            permissions.map((permission) => (
              <span
                key={permission}
                className="rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700"
              >
                {permission}
              </span>
            ))
          ) : (
            <p className="text-sm text-slate-500">
              Permissions are inherited through assigned roles.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
